import { FC, ReactNode } from "react";

import { cn } from "@/lib/utils";
import AiStarIcon from "atoms/icon/icons/ai-start-icon.svg";

import { getPersonaImage } from "../assets/getPersonaImage";

export type AvatarSize = "small" | "medium" | "large" | "xlarge";
export type BadgePosition =
  | "bottom-right"
  | "bottom-left"
  | "top-right"
  | "top-left";

export interface AdvisorAvatarProps {
  advisor: string;
  src?: string;
  alt?: string;
  size?: AvatarSize;
  showBadge?: boolean;
  badgePosition?: BadgePosition;
  className?: string;
  imageClassName?: string;
  badgeClassName?: string;
  backgroundColor?: string;
  badgeBackgroundColor?: string;
  fallback?: ReactNode;
  customBadge?: ReactNode;
}

const sizeClasses: Record<
  AvatarSize,
  { container: string; badge: string; badgePadding: string }
> = {
  small: {
    container: "w-12 h-12",
    badge: "w-5 h-5",
    badgePadding: "p-[3.5px]",
  },
  medium: {
    container: "w-16 h-16",
    badge: "w-8 h-8",
    badgePadding: "p-[4.5px]",
  },
  large: {
    container: "w-24 h-24",
    badge: "w-11 h-11",
    badgePadding: "p-[6.5px]",
  },
  xlarge: {
    container: "w-32 h-32",
    badge: "w-14 h-14",
    badgePadding: "p-[8px]",
  },
};

const badgePositionClasses: Record<BadgePosition, string> = {
  "bottom-right": "bottom-[-10%] right-[-10%]",
  "bottom-left": "bottom-[-10%] left-[-10%]",
  "top-right": "top-[-10%] right-[-10%]",
  "top-left": "top-[-10%] left-[-10%]",
};

export const AdvisorAvatar: FC<AdvisorAvatarProps> = ({
  advisor,
  src,
  size = "large",
  showBadge = true,
  badgePosition = "bottom-right",
  className,
  imageClassName,
  badgeClassName,
  backgroundColor = "#DEC19C",
  badgeBackgroundColor = "#DEC19C",
  fallback,
  customBadge,
}) => {
  const sizes = sizeClasses[size];
  const imageSrc = advisor ? getPersonaImage(advisor).clear : src;
  const imageAlt = `avatar${advisor}`;

  return (
    <div className={cn("relative inline-flex", className)}>
      <div
        className={cn(
          "flex items-center justify-center rounded-full",
          sizes.container
        )}
        style={{ backgroundColor }}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={imageAlt}
            className={cn(
              "h-full w-full rounded-full object-cover",
              imageClassName
            )}
          />
        ) : (
          <div
            className={cn(
              "flex h-full w-full items-center justify-center rounded-full font-medium text-white",
              imageClassName
            )}
          >
            {fallback}
          </div>
        )}
      </div>

      {showBadge && (
        <div
          className={cn(
            "absolute flex items-center justify-center rounded-full",
            badgePositionClasses[badgePosition],
            sizes.badge,
            sizes.badgePadding,
            badgeClassName
          )}
          style={{ backgroundColor: badgeBackgroundColor }}
        >
          {customBadge || (
            <AiStarIcon className="h-full w-full flex-shrink-0" />
          )}
        </div>
      )}
    </div>
  );
};
